import React, {createContext, useContext, useEffect, useState} from "react";
import ClayLoadingIndicator from "@clayui/loading-indicator";
import {fetchTranslations} from "../api/TranslationsApi";
import {PREDEFINED_TRANSLATION_KEYS} from "../util/translationKeys";

interface TranslationContextType {
    t: (key: string, ...args: (string | number)[]) => string;
    languageId: string;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [collectedKeys] = useState<Set<string>>(new Set(PREDEFINED_TRANSLATION_KEYS));
    const [isLoaded, setIsLoaded] = useState(false);
    const [localLanguageId, setLocalLanguageId] = useState(
        Liferay.ThemeDisplay.getLanguageId() || document.documentElement.lang || "en_US"
    );

    useEffect(() => {
        if (collectedKeys.size === 0 || isLoaded) return;

        const loadTranslations = async () => {
            const keysArray = Array.from(collectedKeys);
            console.debug("[Translation] Fetching keys:", keysArray);

            try {
                const response = await fetchTranslations(localLanguageId, keysArray);
                console.debug("TranslationResponse", response);
                const newTranslations = response.translations.reduce((acc, { key, value }) => {
                    acc[key] = value;
                    return acc;
                }, {} as Record<string, string>);

                setTranslations((prev) => ({ ...prev, ...newTranslations }));
                setIsLoaded(true);
            } catch (error) {
                console.error("[Translation] error during fetching:", error);
                setIsLoaded(true);
            }
        };

        loadTranslations();
    }, [collectedKeys, isLoaded, localLanguageId]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const newLang = document.documentElement.lang;
            if (newLang !== localLanguageId) {
                console.debug(`Language changed: ${localLanguageId} -> ${newLang}`);
                setLocalLanguageId(newLang);
                setTranslations({});
                setIsLoaded(false);
            }
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["lang"],
        });

        return () => observer.disconnect();
    }, [localLanguageId]);

    const t = (key: string, ...args: (string | number)[]): string => {
        const translation = translations[key] || key;
        if (args.length === 0) return translation;

        return translation.replace(/{(\d+)}/g, (match, index) =>
            args[index] !== undefined ? String(args[index]) : match
        );
    };

    if (!isLoaded) {
        return <ClayLoadingIndicator/>;
    }

    return (
        <TranslationContext.Provider value={{ t, languageId: localLanguageId }}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error("useTranslation must be used within a TranslationProvider");
    }
    return context;
};
